import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import PageHeader from "src/components/common/PageHeader";
import Form, { FormField } from "src/components/ui/Form";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { toast } from "react-toastify";

const TenantForm: React.FC = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { createTenant, editTenant, tenantDetails } = useAppSelector(
    (state) => state.tenant
  );

  useEffect(() => {
    if (tenantId) {
      dispatch(tenantActions.getTenantDetailsRequest(tenantId));
    } else {
      dispatch(tenantActions.resetTenantDetails());
    }
  }, [dispatch, tenantId]);

  useEffect(() => {
    dispatch(tenantActions.resetCreateTenant());
    dispatch(tenantActions.resetEditTenant());
    return () => {
      dispatch(tenantActions.resetCreateTenant());
      dispatch(tenantActions.resetEditTenant());
    };
  }, [dispatch]);

  useEffect(() => {
    if (tenantDetails.error) {
      toast.error("Failed to fetch tenant details");
    }
  }, [tenantDetails.error]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (createTenant.data) {
      toast.success("Tenant created successfully");
      handleClose();
    } else if (createTenant.error) {
      toast.error(`Error: ${createTenant.error}`);
    }
  }, [createTenant]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editTenant.data) {
      toast.success("Tenant modified successfully");
      handleClose();
    } else if (editTenant.error) {
      toast.error(`Error: ${editTenant.error}`);
    }
  }, [editTenant]);

  const handleClose = () => {
    dispatch(tenantActions.resetCreateTenant());
    dispatch(tenantActions.resetEditTenant());
    navigate("/admin/tenant/list");
  };

  // ✅ Define all form fields
  const fields: FormField[] = [
    {
      label: "Tenant Name",
      name: "tenantName",
      type: "text",
      placeholder: "Enter tenant name",
      validations: { required: true },
    },
    {
      label: "Tenant Email",
      name: "tenantEmail",
      type: "text",
      placeholder: "Enter tenant email address",
      validations: {
        required: true,
        regex: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
        message: "Invalid tenant email format",
      },
    },
    {
      label: "Tenant Address",
      name: "tenantAddress",
      type: "text",
      placeholder: "Enter tenant address",
      validations: { required: true },
    },
    {
      label: "Tenant Company Size",
      name: "tenantCompanySize",
      type: "text",
      placeholder: "5-10, 10-50, 50-100 etc.",
      validations: { required: true },
    },
  ];

  const initialState = useMemo(
    () => ({
      ...tenantDetails.data,
    }),
    [tenantDetails.data]
  );

  // ✅ Form submission handler
  const handleSubmit = (data: Record<string, any>) => {
    if (tenantDetails.data?._id) {
      dispatch(
        tenantActions.editTenantRequest({
          ...(data as any),
          _id: tenantDetails.data?._id,
          // tenantId,
        })
      );
    } else {
      dispatch(tenantActions.createTenantRequest(data));
    }
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: tenantId ? "Edit Tenant | Invyntra" : "Create Tenant | Invyntra",
        description: "Create or edit tenant details.",
        keywords: "tenants, details, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={tenantId ? "Edit Tenant" : "Create Tenant"}
          showBack={!!tenantId}
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Tenants", path: "/admin/tenants/list" },
            { label: tenantId ? "Edit Tenant" : "Create Tenant", path: "" },
          ]}
        />
        <Card>
          <Form
            initialState={initialState as any}
            fields={fields}
            onSubmit={handleSubmit}
            onCancel={() => navigate("/admin/tenants/list")}
            loading={
              createTenant.loading ||
              editTenant.loading ||
              tenantDetails.loading
            }
          />
        </Card>
      </div>
    </Layout>
  );
};

export default TenantForm;
